/**
 * Controlled audit action vocabulary (Story 34.1 — Epic 34).
 *
 * Every audit event's `action` MUST come from `ACTIONS` — no free-form strings
 * (the 34-2 writer validates against `isKnownAction`). The vocabulary is TIERED
 * to the approved 34-3 scope: it covers the epic's core resources PLUS the
 * destructive / data-egress surface that exists in the live codebase. Declaring
 * the full set here is cheap and means 34-3 (and future instrumentation) only
 * references existing constants rather than re-touching this file.
 *
 * `resourceType` mirrors the live resource model: the three public data-egress
 * surfaces are distinct (`portal_link`, `dashboard_share_link`,
 * `brand_share_link`) — all three `*.consumed` actions are the highest-value
 * audit signal (tenant data leaving the boundary).
 */

export const ACTIONS = {
  // ---- Reports ----------------------------------------------------------
  REPORT_CREATED: { action: "report.created", resourceType: "report" },
  REPORT_UPDATED: { action: "report.updated", resourceType: "report" },
  REPORT_DELETED: { action: "report.deleted", resourceType: "report" },
  REPORT_SUBMITTED: { action: "report.submitted", resourceType: "report" },

  // ---- Report templates -------------------------------------------------
  REPORT_TEMPLATE_CREATED: { action: "report_template.created", resourceType: "report_template" },
  REPORT_TEMPLATE_UPDATED: { action: "report_template.updated", resourceType: "report_template" },
  REPORT_TEMPLATE_DELETED: { action: "report_template.deleted", resourceType: "report_template" },
  REPORT_TEMPLATE_CLONED: { action: "report_template.cloned", resourceType: "report_template" },

  // ---- Clients ----------------------------------------------------------
  CLIENT_CREATED: { action: "client.created", resourceType: "client" },
  CLIENT_UPDATED: { action: "client.updated", resourceType: "client" },
  CLIENT_ARCHIVED: { action: "client.archived", resourceType: "client" },
  CLIENT_RESTORED: { action: "client.restored", resourceType: "client" },
  CLIENT_DELETED: { action: "client.deleted", resourceType: "client" },

  // ---- Client sub-resources --------------------------------------------
  CUSTOM_METRIC_CREATED: { action: "custom_metric.created", resourceType: "custom_metric" },
  CUSTOM_METRIC_UPDATED: { action: "custom_metric.updated", resourceType: "custom_metric" },
  CUSTOM_METRIC_DELETED: { action: "custom_metric.deleted", resourceType: "custom_metric" },
  CLIENT_GOAL_CREATED: { action: "client_goal.created", resourceType: "client_goal" },
  CLIENT_GOAL_UPDATED: { action: "client_goal.updated", resourceType: "client_goal" },
  CLIENT_GOAL_DELETED: { action: "client_goal.deleted", resourceType: "client_goal" },
  CLIENT_BUDGET_CREATED: { action: "client_budget.created", resourceType: "client_budget" },
  CLIENT_BUDGET_UPDATED: { action: "client_budget.updated", resourceType: "client_budget" },
  CLIENT_BUDGET_DELETED: { action: "client_budget.deleted", resourceType: "client_budget" },
  CLIENT_MARKUP_CREATED: { action: "client_markup.created", resourceType: "client_markup" },
  CLIENT_MARKUP_UPDATED: { action: "client_markup.updated", resourceType: "client_markup" },
  CLIENT_MARKUP_DELETED: { action: "client_markup.deleted", resourceType: "client_markup" },
  TAG_CREATED: { action: "tag.created", resourceType: "tag" },
  TAG_UPDATED: { action: "tag.updated", resourceType: "tag" },
  TAG_DELETED: { action: "tag.deleted", resourceType: "tag" },

  // ---- Integrations -----------------------------------------------------
  INTEGRATION_CONNECTED: { action: "integration.connected", resourceType: "integration" },
  INTEGRATION_DISCONNECTED: { action: "integration.disconnected", resourceType: "integration" },
  INTEGRATION_DISCARD_REQUESTED: { action: "integration.discard_requested", resourceType: "integration" },

  // ---- Share / portal links (data egress — highest audit value) --------
  PORTAL_LINK_CREATED: { action: "portal_link.created", resourceType: "portal_link" },
  PORTAL_LINK_REVOKED: { action: "portal_link.revoked", resourceType: "portal_link" },
  PORTAL_LINK_CONSUMED: { action: "portal_link.consumed", resourceType: "portal_link" },
  DASHBOARD_SHARE_LINK_CREATED: { action: "dashboard_share_link.created", resourceType: "dashboard_share_link" },
  DASHBOARD_SHARE_LINK_REVOKED: { action: "dashboard_share_link.revoked", resourceType: "dashboard_share_link" },
  DASHBOARD_SHARE_LINK_CONSUMED: { action: "dashboard_share_link.consumed", resourceType: "dashboard_share_link" },
  BRAND_SHARE_LINK_CREATED: { action: "brand_share_link.created", resourceType: "brand_share_link" },
  BRAND_SHARE_LINK_REVOKED: { action: "brand_share_link.revoked", resourceType: "brand_share_link" },
  BRAND_SHARE_LINK_CONSUMED: { action: "brand_share_link.consumed", resourceType: "brand_share_link" },

  // ---- Dashboards & brands ---------------------------------------------
  DASHBOARD_CREATED: { action: "dashboard.created", resourceType: "dashboard" },
  DASHBOARD_UPDATED: { action: "dashboard.updated", resourceType: "dashboard" },
  DASHBOARD_DELETED: { action: "dashboard.deleted", resourceType: "dashboard" },
  BRAND_CREATED: { action: "brand.created", resourceType: "brand" },
  BRAND_UPDATED: { action: "brand.updated", resourceType: "brand" },
  BRAND_DELETED: { action: "brand.deleted", resourceType: "brand" },

  // ---- Schedules, tasks, alerts ----------------------------------------
  SCHEDULE_CREATED: { action: "schedule.created", resourceType: "schedule" },
  SCHEDULE_UPDATED: { action: "schedule.updated", resourceType: "schedule" },
  SCHEDULE_DELETED: { action: "schedule.deleted", resourceType: "schedule" },
  TASK_CREATED: { action: "task.created", resourceType: "task" },
  TASK_UPDATED: { action: "task.updated", resourceType: "task" },
  TASK_DELETED: { action: "task.deleted", resourceType: "task" },
  ALERT_CREATED: { action: "alert.created", resourceType: "alert" },
  ALERT_UPDATED: { action: "alert.updated", resourceType: "alert" },
  ALERT_DELETED: { action: "alert.deleted", resourceType: "alert" },

  // ---- Team & account ---------------------------------------------------
  TEAM_MEMBER_INVITED: { action: "team_member.invited", resourceType: "team_member" },
  TEAM_MEMBER_ROLE_CHANGED: { action: "team_member.role_changed", resourceType: "team_member" },
  TEAM_MEMBER_REMOVED: { action: "team_member.removed", resourceType: "team_member" },
  WORKSPACE_DELETED: { action: "workspace.deleted", resourceType: "workspace" },
  ACCOUNT_DELETION_REQUESTED: { action: "account.deletion_requested", resourceType: "account" },
  ACCOUNT_DELETION_CANCELLED: { action: "account.deletion_cancelled", resourceType: "account" },

  // ---- Settings & branding ---------------------------------------------
  TENANT_SETTINGS_UPDATED: { action: "tenant_settings.updated", resourceType: "tenant_settings" },
  TENANT_BRANDING_UPDATED: { action: "tenant_branding.updated", resourceType: "tenant_branding" },
  USER_SETTINGS_UPDATED: { action: "user_settings.updated", resourceType: "user_settings" },

  // ---- Billing / subscription lifecycle --------------------------------
  BILLING_CHECKOUT_STARTED: { action: "billing.checkout_started", resourceType: "billing" },
  BILLING_CHECKOUT_CANCELED: { action: "billing.checkout_canceled", resourceType: "billing" },
  BILLING_PORTAL_OPENED: { action: "billing.portal_opened", resourceType: "billing" },
  BILLING_INTERVAL_SWITCHED: { action: "billing.interval_switched", resourceType: "billing" },
  BILLING_OVERAGE_CONFIRMED: { action: "billing.overage_confirmed", resourceType: "billing" },
  BILLING_SUBSCRIPTION_ACTIVATED: { action: "billing.subscription_activated", resourceType: "billing" },
  BILLING_SUBSCRIPTION_CHANGED: { action: "billing.subscription_changed", resourceType: "billing" },
  BILLING_SUBSCRIPTION_ENDED: { action: "billing.subscription_ended", resourceType: "billing" },
  BILLING_PAYMENT_FAILED: { action: "billing.payment_failed", resourceType: "billing" },
  BILLING_PAYMENT_RECOVERED: { action: "billing.payment_recovered", resourceType: "billing" },

  // ---- Audit self-audit (34-4 admin cold-read) -------------------------
  AUDIT_COLD_ACCESSED: { action: "audit.cold_accessed", resourceType: "audit" },
} as const satisfies Record<string, { action: string; resourceType: string }>;

export type ActionKey = keyof typeof ACTIONS;
export type Action = (typeof ACTIONS)[ActionKey]["action"];
export type ResourceType = (typeof ACTIONS)[ActionKey]["resourceType"];

/** All known action strings, frozen. */
export const KNOWN_ACTIONS: ReadonlySet<string> = new Set(
  Object.values(ACTIONS).map((a) => a.action),
);

/** Type guard: is `s` a vocabulary-controlled action string? */
export function isKnownAction(s: string): s is Action {
  return KNOWN_ACTIONS.has(s);
}

/** Resolve the canonical resourceType for an action string (undefined if unknown). */
export function resourceTypeForAction(action: string): ResourceType | undefined {
  for (const entry of Object.values(ACTIONS)) {
    if (entry.action === action) return entry.resourceType as ResourceType;
  }
  return undefined;
}
